import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Mail, Loader2 } from "lucide-react";
import { Button } from "../ui/button";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "../ui/dialog";
import {
    listEmailProviders,
    listEmailAccounts,
    addEmailAccount,
    oauthMicrosoftStart,
} from "../../lib/api";
import { ADV_INPUT_CLS } from "./utils";

export default function AddAccountDialog({ open, onOpenChange, onCreated }) {
    const [providers, setProviders] = useState([]);
    const [provider, setProvider] = useState("godaddy");
    const [emailAddr, setEmailAddr] = useState("");
    const [password, setPassword] = useState("");
    const [displayName, setDisplayName] = useState("");
    const [showAdvanced, setShowAdvanced] = useState(false);
    const [override, setOverride] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [oauthBusy, setOauthBusy] = useState("");

    useEffect(() => {
        if (!open) return;
        listEmailProviders().then(setProviders).catch(() => {});
        setProvider("godaddy"); setEmailAddr(""); setPassword(""); setDisplayName(""); setShowAdvanced(false); setOverride({});
    }, [open]);

    // Listen for the OAuth callback popup's postMessage so we can refresh
    // the account list as soon as the user completes consent.
    useEffect(() => {
        if (!open) return;
        async function onMsg(ev) {
            const d = ev.data;
            if (!d || typeof d !== "object") return;
            if (d.provider === "microsoft" && d.ok) {
                toast.success(`${d.email} connected via Microsoft 365`);
                try {
                    const all = await listEmailAccounts();
                    const created = all.find(
                        (a) => a.provider === "microsoft" &&
                            a.email_address === (d.email || "").toLowerCase(),
                    ) || all[all.length - 1];
                    if (created) onCreated(created);
                } catch {
                    // Even if list fetch fails, close the dialog and let
                    // the parent re-poll on next mount.
                }
                onOpenChange(false);
            } else if (d.provider === "microsoft" && d.ok === false) {
                toast.error(`Microsoft connection failed: ${d.error || "unknown"}`);
                setOauthBusy("");
            }
        }
        window.addEventListener("message", onMsg);
        return () => window.removeEventListener("message", onMsg);
    }, [open, onCreated, onOpenChange]);

    const connectMicrosoft = async () => {
        setOauthBusy("microsoft");
        try {
            const url = await oauthMicrosoftStart();
            if (!url) throw new Error("No authorize URL");
            const popup = window.open(
                url, "ms-oauth",
                "width=520,height=720,menubar=no,toolbar=no,location=no,status=no",
            );
            if (!popup) {
                toast.error("Allow popups for this site, then try again.");
                setOauthBusy("");
                return;
            }
            // Two paths to know we're done:
            //   1. The callback page posts a message to window.opener.
            //   2. On mobile Chrome / iOS Safari window.opener can be
            //      null and the popup opens as a new tab — in that case
            //      we detect tab close, then aggressively re-list
            //      accounts and pick up whichever one was just added.
            const startCount = (await listEmailAccounts().catch(() => []))
                .filter((a) => a.provider === "microsoft").length;
            const timer = setInterval(async () => {
                if (popup.closed) {
                    clearInterval(timer);
                    setOauthBusy("");
                    // Re-fetch — if a new Microsoft account appeared,
                    // surface it the same way the postMessage path does.
                    try {
                        const all = await listEmailAccounts();
                        const msAccounts = all.filter((a) => a.provider === "microsoft");
                        if (msAccounts.length > startCount) {
                            const created = msAccounts[msAccounts.length - 1];
                            toast.success(`${created.email_address} connected via Microsoft 365`);
                            onCreated(created);
                            onOpenChange(false);
                        }
                    } catch {
                        // ignore — user can refresh manually
                    }
                }
            }, 800);
        } catch (err) {
            toast.error(err?.response?.data?.detail || err?.message || "Microsoft sign-in failed");
            setOauthBusy("");
        }
    };

    const preset = providers.find((p) => p.key === provider) || {};

    const submit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            const payload = {
                provider,
                email_address: emailAddr.trim().toLowerCase(),
                password,
                display_name: displayName.trim(),
                ...override,
            };
            const a = await addEmailAccount(payload);
            toast.success(`${a.email_address} connected`);
            onCreated(a);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't add account");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="bg-[#0A1128] border border-[#2A3655] text-white max-w-md max-h-[90vh] overflow-y-auto"
                data-testid="email-add-account-dialog"
            >
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 text-white">
                        <Mail className="w-5 h-5 text-[#00A3FF]" /> Add email account
                    </DialogTitle>
                </DialogHeader>
                <form onSubmit={submit} className="space-y-3">
                    {/* iter-103: One-click OAuth providers, rendered above the
                        IMAP/SMTP form so they're the path of least resistance. */}
                    <div className="space-y-2 pb-3 border-b border-[#1C253C]">
                        <div className="text-[10px] uppercase tracking-[0.18em] text-slate-400 font-bold">
                            One-click sign in
                        </div>
                        <button
                            type="button"
                            onClick={connectMicrosoft}
                            disabled={oauthBusy === "microsoft"}
                            data-testid="connect-microsoft-365"
                            className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-sm bg-[#0078D4] hover:bg-[#106EBE] disabled:bg-[#1C253C] disabled:cursor-not-allowed border border-[#0078D4] hover:border-white text-white text-sm font-semibold transition-colors"
                        >
                            <svg width="18" height="18" viewBox="0 0 23 23" aria-hidden="true">
                                <rect x="1" y="1" width="10" height="10" fill="#F25022"/>
                                <rect x="12" y="1" width="10" height="10" fill="#7FBA00"/>
                                <rect x="1" y="12" width="10" height="10" fill="#00A4EF"/>
                                <rect x="12" y="12" width="10" height="10" fill="#FFB900"/>
                            </svg>
                            {oauthBusy === "microsoft"
                                ? "Waiting for Microsoft…"
                                : "Connect Microsoft 365 / Outlook"}
                        </button>
                        <div className="text-[10px] text-slate-500 uppercase tracking-[0.18em] font-bold text-center pt-1">
                            — or use IMAP / SMTP —
                        </div>
                    </div>
                    <div>
                        <label className="text-[10px] uppercase tracking-[0.18em] text-slate-400 font-bold block mb-1">Provider</label>
                        <select
                            value={provider}
                            onChange={(e) => setProvider(e.target.value)}
                            data-testid="email-add-provider"
                            className="w-full bg-[#05091A] border border-[#2A3655] rounded-sm px-3 py-2 text-sm text-white focus:outline-none focus:border-[#00A3FF]"
                        >
                            {providers.map((p) => (
                                <option key={p.key} value={p.key}>{p.label}</option>
                            ))}
                        </select>
                        {preset.notes && (
                            <p className="text-[11px] text-amber-300/90 mt-1.5 italic">
                                {preset.notes}
                            </p>
                        )}
                    </div>
                    <div>
                        <label className="text-[10px] uppercase tracking-[0.18em] text-slate-400 font-bold block mb-1">
                            Email address <span className="text-red-400">*</span>
                        </label>
                        <input
                            type="email"
                            value={emailAddr}
                            onChange={(e) => setEmailAddr(e.target.value)}
                            placeholder="you@yourcompany.com"
                            data-testid="email-add-email"
                            required
                            className="w-full bg-[#05091A] border border-[#2A3655] rounded-sm px-3 py-2 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-[#00A3FF]"
                        />
                    </div>
                    <div>
                        <label className="text-[10px] uppercase tracking-[0.18em] text-slate-400 font-bold block mb-1">
                            Password <span className="text-red-400">*</span>
                        </label>
                        <input
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            placeholder="••••••••"
                            data-testid="email-add-password"
                            required
                            autoComplete="off"
                            className="w-full bg-[#05091A] border border-[#2A3655] rounded-sm px-3 py-2 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-[#00A3FF]"
                        />
                    </div>
                    <div>
                        <label className="text-[10px] uppercase tracking-[0.18em] text-slate-400 font-bold block mb-1">
                            Display name (optional)
                        </label>
                        <input
                            type="text"
                            value={displayName}
                            onChange={(e) => setDisplayName(e.target.value)}
                            placeholder="Your Name / Company"
                            data-testid="email-add-display-name"
                            className="w-full bg-[#05091A] border border-[#2A3655] rounded-sm px-3 py-2 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-[#00A3FF]"
                        />
                    </div>
                    <details
                        open={showAdvanced || provider === "custom"}
                        onToggle={(e) => setShowAdvanced(e.target.open)}
                        className="border border-[#2A3655] rounded-sm p-2"
                    >
                        <summary className="cursor-pointer text-xs font-bold text-slate-300 uppercase tracking-wider">
                            Advanced — IMAP/SMTP overrides
                        </summary>
                        <div className="grid grid-cols-2 gap-2 mt-2 text-xs">
                            <input placeholder={`IMAP host (${preset.imap_host || "host"})`} className={ADV_INPUT_CLS}
                                value={override.imap_host || ""} onChange={(e) => setOverride((o) => ({ ...o, imap_host: e.target.value || undefined }))} />
                            <input placeholder={`IMAP port (${preset.imap_port || 993})`} className={ADV_INPUT_CLS}
                                value={override.imap_port || ""} onChange={(e) => setOverride((o) => ({ ...o, imap_port: e.target.value ? parseInt(e.target.value, 10) : undefined }))} />
                            <input placeholder={`SMTP host (${preset.smtp_host || "host"})`} className={ADV_INPUT_CLS}
                                value={override.smtp_host || ""} onChange={(e) => setOverride((o) => ({ ...o, smtp_host: e.target.value || undefined }))} />
                            <input placeholder={`SMTP port (${preset.smtp_port || 465})`} className={ADV_INPUT_CLS}
                                value={override.smtp_port || ""} onChange={(e) => setOverride((o) => ({ ...o, smtp_port: e.target.value ? parseInt(e.target.value, 10) : undefined }))} />
                        </div>
                        <p className="text-[10px] text-slate-500 mt-2 italic">
                            Leave blank to use the preset defaults.
                        </p>
                    </details>
                    <div className="flex items-center justify-end gap-2 pt-2 border-t border-[#2A3655]">
                        <button
                            type="button"
                            onClick={() => onOpenChange(false)}
                            disabled={submitting}
                            className="px-4 py-2 rounded-sm border border-[#2A3655] text-slate-300 hover:border-slate-500 text-xs font-bold uppercase tracking-wider disabled:opacity-50"
                        >
                            Cancel
                        </button>
                        <Button
                            type="submit"
                            disabled={submitting}
                            data-testid="email-add-submit"
                            className="bg-[#00A3FF] hover:bg-[#3395FF] text-white font-bold h-9 px-4 disabled:opacity-50"
                        >
                            {submitting ? (
                                <><Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" /> Verifying…</>
                            ) : "Verify & save"}
                        </Button>
                    </div>
                </form>
            </DialogContent>
        </Dialog>
    );
}
